import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Business & Ventures — the owner-level venture portfolio.
 *
 * A Venture moves idea → experiment → active → paused → closed. Life OS holds
 * OWNER-LEVEL truth and summaries only (not full company operations / ERP).
 * Revenue is a coarse monthly snapshot in integer minor units so the math is
 * exact; detailed ledgers live in Finance, linked rather than duplicated.
 */

export type VentureStatus = 'idea' | 'experiment' | 'active' | 'paused' | 'closed';
export const VENTURE_STATUSES: readonly VentureStatus[] = ['idea', 'experiment', 'active', 'paused', 'closed'] as const;

export interface Venture extends RecordEnvelope {
  readonly kind: 'venture';
  readonly title: string;
  /** Free-form sector label (saas, content, consulting, …). */
  readonly sector: string;
  readonly status: VentureStatus;
  /** Coarse monthly revenue snapshot in minor units (cents). */
  readonly monthlyRevenueMinor: number;
  readonly note: string;
  /** Linked projects/income streams/finance accounts (ids only). */
  readonly linkedIds: readonly EntityId[];
}

export interface VentureInput {
  readonly title: string;
  readonly sector?: string;
  readonly note?: string;
  readonly privacyClass?: PrivacyClass;
}

export interface VenturePatch {
  readonly title?: string;
  readonly sector?: string;
  readonly note?: string;
  readonly monthlyRevenueMinor?: number;
}

/** A venture is "live" while it is being experimented on or actively run. */
export function isVentureLive(status: VentureStatus): boolean {
  return status === 'experiment' || status === 'active';
}

export interface VenturePortfolioSummary {
  readonly total: number;
  readonly live: number;
  /** Sum of monthly revenue across active ventures, minor units. */
  readonly activeMonthlyRevenueMinor: number;
  readonly byStatus: Record<VentureStatus, number>;
}

/** Pure portfolio roll-up. Revenue counts only ventures in the active status. */
export function summarizeVentures(
  ventures: readonly Pick<Venture, 'status' | 'monthlyRevenueMinor'>[],
): VenturePortfolioSummary {
  const byStatus: Record<VentureStatus, number> = { idea: 0, experiment: 0, active: 0, paused: 0, closed: 0 };
  let live = 0;
  let activeMonthlyRevenueMinor = 0;
  for (const v of ventures) {
    byStatus[v.status] += 1;
    if (isVentureLive(v.status)) live += 1;
    if (v.status === 'active') activeMonthlyRevenueMinor += Math.trunc(v.monthlyRevenueMinor);
  }
  return { total: ventures.length, live, activeMonthlyRevenueMinor, byStatus };
}
